import builder from "@rsql/builder";
import { emit } from "@rsql/emitter";
import { parse } from "@rsql/parser";
import {
	ColDef,
	DateFilterModel,
	ICombinedSimpleModel,
	ISimpleFilterModel,
	NumberFilterModel,
	SetFilterModel,
	TextFilterModel,
} from "ag-grid-community";
import { IS_NOT_NULL, IS_NULL } from "../Rsql/ast";

/**
 * Stable, non-empty group key for the "(None)"/"Geen" bucket. AG Grid's SSRM `groupKeys` are strings and
 * a null/blank group value cannot round-trip (the group never issues a proper leaf request). The group
 * columns' `keyCreator` maps null/blank values to this sentinel so the null group is drillable; both
 * `groupKeysToRSQL` (leaf drilldown) and the backend's parent-group criteria translate it back to an
 * `IS NULL` filter. Kept in sync with the backend constant in GridDataController.
 */
export const GROUP_NULL_KEY = "__PULSEFOOD_GROUP_NULL__";

type GridFilterType =
	| "equals"
	| "notEqual"
	| "contains"
	| "notContains"
	| "startsWith"
	| "endsWith"
	| "lessThan"
	| "lessThanOrEqual"
	| "greaterThan"
	| "greaterThanOrEqual"
	| "inRange"
	| "blank"
	| "notBlank"
	| "empty"
	| "in"
	| "notIn"
	| "simpleSearch"
	| "advancedSearch";

/**
 * Assertions
 */
export function isCombinedFilter(filter: ISimpleFilterModel): filter is ICombinedSimpleModel<ISimpleFilterModel> {
	return "operator" in filter && (filter.operator === "AND" || filter.operator === "OR");
}

export function isTextFilter(filter: ISimpleFilterModel): filter is TextFilterModel {
	return filter.filterType === "text";
}

export function isNumberFilter(filter: ISimpleFilterModel): filter is NumberFilterModel {
	return filter.filterType === "number";
}

export function isDateFilter(filter: ISimpleFilterModel): filter is DateFilterModel {
	return filter.filterType === "date";
}

export function isSetFilter(filter: ISimpleFilterModel): filter is SetFilterModel {
	return filter.filterType === "set";
}

/**
 * Convert an ag-grid filter model to an RSQL string.
 */
export function convertFilterModelToRSQLString(
	filterModel: Record<string, ICombinedSimpleModel<ISimpleFilterModel> | ISimpleFilterModel>,
	columnDefs: ColDef[],
) {
	const comparisonNodes = Object.entries(filterModel)
		.map(([key, fm]) => {
			const colDef = columnDefs.find((c) => c.field == key) as ColDef;
			if (isCombinedFilter(fm) && fm.conditions?.length) {
				if (fm.operator === "OR") {
					return builder.or(
						...fm.conditions.map((condition) => {
							return filterModelToComparisonNode(key, condition, colDef)!;
						}),
					);
				} else if (fm.operator === "AND") {
					return builder.and(
						...fm.conditions.map((condition) => {
							return filterModelToComparisonNode(key, condition, colDef)!;
						}),
					);
				}
			}

			return filterModelToComparisonNode(key, fm, colDef);
		})
		.filter((node): node is NonNullable<typeof node> => node != null);

	if (comparisonNodes.length > 0) {
		return emit(builder.and(...comparisonNodes));
	}

	return "";
}

/**
 *
 */
export function filterModelToComparisonNode(filterKey: string, filter: ISimpleFilterModel, colDef: ColDef) {
	const rsqlFor = (colDef?.context as { rsqlFor?: (filter: ISimpleFilterModel) => string | null | undefined })?.rsqlFor;
	if (rsqlFor) {
		const fragment = rsqlFor(filter);
		return fragment ? parse(fragment) : null;
	}
	if (isTextFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.filter, filter.filterTo],
			filter.type,
		);
	} else if (isNumberFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.filter, filter.filterTo],
			filter.type,
		);
	} else if (isDateFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.dateFrom, filter.dateTo],
			filter.type,
		);
	} else if (isSetFilter(filter)) {
		if ("collection" in colDef.filterParams) {
			return builder.or(...filter.values.map((value) => builder.comparison(filterKey, "=contains=", value as string)));
		}

		// One value and it's falsy
		if (filter.values.length === 1 && !filter.values[0]) {
			return builder.comparison(filterKey, IS_NULL, "");
		}

		// Multiple values and one of them is null
		if (filter.values.includes(null)) {
			return builder.or(
				builder.comparison(filterKey, IS_NULL, ""),
				builder.in(filterKey, filter.values.filter((v) => !!v).map(String)),
			);
		}

		// No null values
		return getComparisonNode(filterKey, filter.values.map(String), "in");
	} else {
		throw new Error(`Unknown filter type: '${filter.filterType}' provided!`);
	}
}

/**
 * Read a (possibly nested) value out of a row object by a dotted path, mirroring how ag-grid resolves
 * a colDef `field` like `"product.name"`.
 */
export function getNested(obj: any, path: string): any {
	if (obj == null) return undefined;
	return path.split(".").reduce((acc, part) => (acc == null ? undefined : acc[part]), obj);
}

/**
 * Build an RSQL equality string constraining a row to the already-expanded parent group keys, so leaf
 * rows can be fetched from the normal index endpoint. Uses each column's `context.groupKeyPath`
 * (the id path for entity references) falling back to its field.
 */
export function groupKeysToRSQL(
	rowGroupCols: { id?: string; field?: string }[],
	groupKeys: (string | null)[],
	colDefs: ColDef[],
): string {
	const nodes = groupKeys.map((key, i) => {
		const groupCol = rowGroupCols[i] ?? {};
		const colDef = colDefs.find(
			(c) => c.colId === groupCol.id || c.field === groupCol.field || c.field === groupCol.id,
		);
		const ctx = colDef?.context as any;
		const keyPath = (ctx?.groupKeyPath as string) || groupCol.field || groupCol.id || "";
		if (key === null || key === undefined || key === "" || key === GROUP_NULL_KEY) {
			// Null "(None)"/"Geen" group: null-check the association (join-free FK check) for references.
			// The GROUP_NULL_KEY sentinel is what a null group's `keyCreator` emits so the group round-trips.
			return builder.comparison((ctx?.groupNullPath as string) || keyPath, IS_NULL, "");
		}
		return builder.eq(keyPath, key);
	});

	if (nodes.length === 0) return "";
	if (nodes.length === 1) return emit(nodes[0]);
	return emit(builder.and(...nodes));
}

export function getComparisonNode(key: string, filter: (string | number)[], filterType: GridFilterType) {
	switch (filterType) {
		case "equals":
			return builder.eq(key, filter[0]);
		case "notEqual":
			return builder.neq(key, filter[0]);
		case "contains":
			return builder.eq(key, `*${filter[0]}*`);
		case "notContains":
			return builder.neq(key, `*${filter[0]}*`);
		case "startsWith":
			return builder.eq(key, `${filter[0]}*`);
		case "endsWith":
			return builder.eq(key, `*${filter[0]}`);
		case "lessThan":
			return builder.lt(key, filter[0]);
		case "lessThanOrEqual":
			return builder.le(key, filter[0]);
		case "greaterThan":
			return builder.gt(key, filter[0]);
		case "greaterThanOrEqual":
			return builder.ge(key, filter[0]);
		case "inRange":
			return builder.logic([builder.ge(key, filter[0]), builder.le(key, filter[1])], "and");
		case "blank":
			return builder.comparison(key, IS_NULL, "");
		case "notBlank":
			return builder.comparison(key, IS_NOT_NULL, "");
		case "empty":
			return builder.eq(key, "");
		case "in":
			return builder.in(key, filter);
		case "simpleSearch": {
			const terms = (filter[0] as string)
				?.trim()
				.split(" ")
				.map((it) => builder.comparison(key, "==", `*${it}*`));

			return builder.and(...terms);
		}
		case "advancedSearch":
			return builder.comparison(key, "=search=", filter[0]);
		default:
			throw new Error("Invalid filter type provided!");
	}
}

/* ------------------------------------------------------------------------------------------------ *
 * RSQL -> ag-grid filter model (inverse of convertFilterModelToRSQLString).
 * Used by AppDataGrid to render an assistant-supplied RSQL criteria as editable filter chips.
 * Best-effort: returns null if the RSQL uses a construct we can't map to a chip, so the caller can
 * fall back gracefully. The agent emits exactly the operator set the forward converter produces, so
 * that null path is an edge case.
 * ------------------------------------------------------------------------------------------------ */

// Loose AST typing — @rsql/parser node shapes accessed structurally to avoid coupling to export names.
type RsqlNode = any;

const isComparison = (n: RsqlNode) => n?.type === "COMPARISON";
const isLogic = (n: RsqlNode) => n?.type === "LOGIC";
const logicIsAnd = (n: RsqlNode) => n.operator === ";" || String(n.operator).toUpperCase() === "AND";

/** Flatten a chain of AND logic nodes into its conjuncts (OR nodes / comparisons are kept intact). */
function flattenAnd(node: RsqlNode): RsqlNode[] {
	if (isLogic(node) && logicIsAnd(node)) return [...flattenAnd(node.left), ...flattenAnd(node.right)];
	return [node];
}

/** Flatten a chain of logic nodes sharing one join operator into its leaves. */
function flattenJoin(node: RsqlNode, isAnd: boolean): RsqlNode[] {
	if (isLogic(node) && logicIsAnd(node) === isAnd) {
		return [...flattenJoin(node.left, isAnd), ...flattenJoin(node.right, isAnd)];
	}
	return [node];
}

/** The single selector a node constrains, or null if it spans multiple fields. */
function nodeField(node: RsqlNode): string | null {
	if (isComparison(node)) return node.left.selector;
	if (isLogic(node)) {
		const l = nodeField(node.left);
		const r = nodeField(node.right);
		return l && r && l === r ? l : null;
	}
	return null;
}

/** ag-grid `colDef.filter` -> simple filter type. */
function gridFilterType(colDef: ColDef): "text" | "number" | "date" | "set" | null {
	switch (colDef.filter) {
		case "agTextColumnFilter":
			return "text";
		case "agNumberColumnFilter":
			return "number";
		case "agDateColumnFilter":
			return "date";
		case "agSetColumnFilter":
			return "set";
		default:
			return null;
	}
}

const RANGE_OPERATORS: Record<string, string> = {
	"=gt=": "greaterThan",
	"=ge=": "greaterThanOrEqual",
	"=lt=": "lessThan",
	"=le=": "lessThanOrEqual",
	">": "greaterThan",
	">=": "greaterThanOrEqual",
	"<": "lessThan",
	"<=": "lessThanOrEqual",
	"==": "equals",
	"!=": "notEqual",
};

/** Map one comparison (operator + raw value) to a simple text/number/date filter, or null if unmappable. */
function comparisonToSimple(operator: string, rawValue: string, filterType: "text" | "number" | "date"): any | null {
	if (operator === IS_NULL) return { filterType, type: "blank" };
	if (operator === IS_NOT_NULL) return { filterType, type: "notBlank" };

	if (filterType === "date") {
		const type = RANGE_OPERATORS[operator];
		if (!type) return null;
		return { filterType: "date", type, dateFrom: toGridDate(rawValue), dateTo: null };
	}
	if (filterType === "number") {
		const type = RANGE_OPERATORS[operator];
		if (!type) return null;
		return { filterType: "number", type, filter: Number(rawValue), filterTo: null };
	}

	// text: invert the wildcard convention used by getComparisonNode.
	if (operator !== "==" && operator !== "!=") return null;
	const negated = operator === "!=";
	const starts = rawValue.startsWith("*");
	const ends = rawValue.endsWith("*");
	if (starts && ends)
		return { filterType: "text", type: negated ? "notContains" : "contains", filter: rawValue.slice(1, -1) };
	if (ends) return { filterType: "text", type: negated ? "notContains" : "startsWith", filter: rawValue.slice(0, -1) };
	if (starts) return { filterType: "text", type: negated ? "notContains" : "endsWith", filter: rawValue.slice(1) };
	return { filterType: "text", type: negated ? "notEqual" : "equals", filter: rawValue };
}

/** Bare `yyyy-MM-dd` -> the `yyyy-MM-ddT00:00:00` shape the app's date filters use (see processFilterModel). */
function toGridDate(value: string): string {
	return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
}

/** Collect a set filter's values from a comparison or an OR-chain of comparisons (handles the null shape). */
function collectSetValues(node: RsqlNode): (string | null)[] | null {
	if (isComparison(node)) {
		const op = node.operator;
		const val = node.right.value;
		if (op === IS_NULL) return [null];
		if (op === "=in=" || op === "=out=") return Array.isArray(val) ? val : [val];
		if (op === "==" || op === "=contains=") return [val === "" ? null : val];
		return null;
	}
	if (isLogic(node)) {
		const left = collectSetValues(node.left);
		const right = collectSetValues(node.right);
		if (!left || !right) return null;
		return [...left, ...right];
	}
	return null;
}

/** Convert all nodes constraining one field into a single ag-grid filter-model entry. */
function fieldFilter(nodes: RsqlNode[], filterType: "text" | "number" | "date" | "set"): any | null {
	if (filterType === "set") {
		const values: (string | null)[] = [];
		for (const n of nodes) {
			const collected = collectSetValues(n);
			if (!collected) return null;
			values.push(...collected);
		}
		return { filterType: "set", values };
	}

	// text/number/date: build a simple condition per comparison, combine multiples (e.g. ge+le range).
	// A same-field group (`(x=="a",x=="b")`) arrives as a LOGIC node: flatten it and keep its join
	// operator, which ag-grid represents natively as an ICombinedSimpleModel.
	const conditions: any[] = [];
	let joinOperator: "AND" | "OR" = "AND";
	for (const n of nodes) {
		const leaves = isLogic(n) ? flattenJoin(n, logicIsAnd(n)) : [n];
		if (isLogic(n)) joinOperator = logicIsAnd(n) ? "AND" : "OR";
		for (const leaf of leaves) {
			if (!isComparison(leaf)) return null;
			const simple = comparisonToSimple(leaf.operator, leaf.right.value, filterType);
			if (!simple) return null;
			conditions.push(simple);
		}
	}

	if (conditions.length === 1) return conditions[0];

	// Two AND-joined bounds on one field -> a single range chip (never fold an OR).
	if (joinOperator === "AND" && conditions.length === 2 && (filterType === "date" || filterType === "number")) {
		const lower = conditions.find((c) => c.type === "greaterThanOrEqual" || c.type === "greaterThan");
		const upper = conditions.find((c) => c.type === "lessThanOrEqual" || c.type === "lessThan");
		if (lower && upper) {
			if (filterType === "date") {
				return { filterType: "date", type: "inRange", dateFrom: lower.dateFrom, dateTo: upper.dateFrom };
			}
			return { filterType: "number", type: "inRange", filter: lower.filter, filterTo: upper.filter };
		}
	}

	// Otherwise a combined AND/OR of conditions on the same field.
	return { filterType, operator: joinOperator, conditions };
}

/**
 * Convert an RSQL string to an ag-grid filter model, using `columnDefs` to pick each field's filter type.
 * Returns null if any part can't be mapped (caller should fall back rather than apply a partial filter).
 */
export function convertRSQLStringToFilterModel(
	rsql: string,
	columnDefs: ColDef[],
): Record<string, ICombinedSimpleModel<ISimpleFilterModel> | ISimpleFilterModel> | null {
	if (!rsql || !rsql.trim()) return {};

	let ast: RsqlNode;
	try {
		ast = parse(rsql);
	} catch {
		return null;
	}

	// Group each field's conjuncts together (a single field may have both a >= and a <= bound).
	const byField = new Map<string, RsqlNode[]>();
	for (const conjunct of flattenAnd(ast)) {
		const field = nodeField(conjunct);
		if (!field) return null;
		(byField.get(field) ?? byField.set(field, []).get(field)!).push(conjunct);
	}

	const model: Record<string, any> = {};
	for (const [field, nodes] of byField) {
		const colDef = columnDefs.find((c) => c.field === field);
		const type = colDef ? gridFilterType(colDef) : null;
		if (!type) return null; // unknown/unfilterable field -> can't render as a chip
		const fm = fieldFilter(nodes, type);
		if (!fm) return null;
		model[field] = fm;
	}
	return model;
}
